/**
 * lx-session-list —— 会话列表（首页）
 *
 * 每行展示：头像 / 对方名 / 最后一条消息 / 时间 / 未读红点。
 * 订阅 session:list-changed 自动重载 —— 所以 AI 发一条消息后，
 * 首页的预览文字和时间会自动更新，不需要任何跨组件调用。
 */

import { LxElement, define } from '@/core/base-element';
import { sessionsRepo, type SessionView } from '@/data/repo.sessions';
import { pushOverlay } from '@/core/ui-store';

export class LxSessionList extends LxElement {
  private _sessions: SessionView[] = [];
  private _loading = true;

  protected styles(): string {
    return `
      :host { display: block; width: 100%; }

      .loading {
        padding: var(--sp-6);
        text-align: center;
        color: var(--c-fg-subtle);
        font-size: var(--fs-sm);
      }

      .row {
        display: flex;
        align-items: center;
        gap: var(--sp-3);
        width: 100%;
        padding: var(--sp-3) var(--layout-content-pad-x);
        text-align: left;
        transition: background var(--dur-fast) var(--ease-standard);
      }

      .row:hover { background: var(--c-surface-2); }
      .row.pinned { background: var(--c-surface); }

      .avatar {
        flex-shrink: 0;
        width: 46px;
        height: 46px;
        border-radius: var(--radius-full);
        display: grid;
        place-items: center;
        font-size: var(--fs-lg);
        font-weight: var(--fw-medium);
        /* 用中性表面色而非主色 —— 一屏 7 个头像都刷成荧光绿会
           把本就稀缺的强调色用滥，主色要留给「未读」这类真正的信号 */
        color: var(--c-fg-muted);
        background: var(--c-surface-2);
        user-select: none;
      }

      /* 置顶会话的头像用主色描边，是比整块填充更克制的强调 */
      .row.pinned .avatar {
        box-shadow: inset 0 0 0 1.5px var(--c-accent);
      }

      .meta {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }

      .top-line {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--sp-2);
      }

      .name {
        font-size: var(--fs-md);
        font-weight: var(--fw-medium);
        color: var(--c-fg);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .time {
        flex-shrink: 0;
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
      }

      .bottom-line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--sp-2);
      }

      .preview {
        font-size: var(--fs-sm);
        color: var(--c-fg-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* 未读红点。用主色而非红色 —— 红色留给错误，视觉语义要分清 */
      .unread {
        flex-shrink: 0;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: var(--radius-full);
        background: var(--c-accent);
        color: var(--c-accent-fg);
        font-size: 11px;
        font-weight: var(--fw-medium);
        display: grid;
        place-items: center;
      }

      .divider {
        height: 1px;
        margin-left: calc(var(--layout-content-pad-x) + 58px);
        background: var(--c-border);
      }
    `;
  }

  protected render(): string {
    if (this._loading) return '<p class="loading">正在读取</p>';
    if (this._sessions.length === 0) return '';

    return this._sessions
      .map((s, i) => {
        const name = s.contact?.remark || s.contact?.name || '未知联系人';
        const initial = name.charAt(0).toUpperCase();

        return `
          ${i > 0 ? '<div class="divider"></div>' : ''}
          <button class="row ${s.pinned ? 'pinned' : ''}" type="button" data-id="${s.id}">
            <span class="avatar">${this.esc(initial)}</span>
            <span class="meta">
              <span class="top-line">
                <span class="name">${this.esc(name)}</span>
                <span class="time">${this.esc(formatTime(s.lastMessageAt))}</span>
              </span>
              <span class="bottom-line">
                <span class="preview">${this.esc(s.lastPreview || '还没有消息')}</span>
                ${s.unread > 0 ? `<span class="unread">${s.unread > 99 ? '99+' : s.unread}</span>` : ''}
              </span>
            </span>
          </button>
        `;
      })
      .join('');
  }

  protected onMounted(): void {
    this.$$('.row').forEach((row) => {
      row.addEventListener('click', () => {
        const id = row.getAttribute('data-id');
        if (!id) return;
        pushOverlay({ kind: 'chat', refId: id });
        void sessionsRepo.markRead(id);
      });
    });
  }

  private async load(): Promise<void> {
    this._sessions = await sessionsRepo.listWithContact();
    this._loading = false;
    this.update();
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    this.subscribe('session:list-changed', () => void this.load());
  }
}

/** 时间显示规则：今天显示时分，昨天显示「昨天」，更早显示月/日 */
function formatTime(ts: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return '昨天';
  }

  if (d.getFullYear() !== now.getFullYear()) {
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  }
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

define('lx-session-list', LxSessionList);
